import { Helmet } from "react-helmet-async";

const SITE_URL =
  process.env.REACT_APP_PUBLIC_SITE_URL || "https://www.trawelldmc.com";

const DEFAULTS = {
  title:
    "Premium DMC in Dubai | MICE & Corporate Travel — Trawelldmc",
  description:
    "Dubai-based Destination Management Company with 20+ years of expertise. Serving 140,000+ travellers across Dubai, Singapore, Malaysia, Thailand, Hong Kong, Vietnam & Philippines. MICE, incentive & corporate travel specialists. Request a quote today.",
  image: `${SITE_URL}/trawelldmc-logo-hd.png`,
  keywords:
    "Destination Management Company, DMC Dubai, MICE Dubai, MICE Asia, corporate travel, incentive travel, Dubai DMC, Singapore DMC, Malaysia DMC, Thailand DMC, Hong Kong DMC, Philippines DMC, Vietnam DMC, South East Asia travel, West Asia travel, conference management, dealer incentive trips",
  twitterTitle: "Premium DMC in Dubai | Trawelldmc",
  twitterDescription: "20+ years. 140,000+ travellers. MICE & corporate travel across Dubai & Southeast Asia.",
  ogDescription: "Dubai-based DMC. 20+ years. 140,000+ travellers. MICE, incentive & corporate travel across Dubai and Southeast Asia.",
};

const ORG_LD = {
  "@context": "https://schema.org",
  "@type": ["TravelAgency", "LocalBusiness"],
  "@id": `${SITE_URL}/#organization`,
  name: "Trawelldmc",
  description:
    "Premium Destination Management Company for South East Asia and West Asia. Dubai headquartered. 20+ years experience, 140,000+ travellers served.",
  url: SITE_URL,
  logo: `${SITE_URL}/trawelldmc-logo-hd.png`,
  telephone: "+971524526313",
  address: {
    "@type": "PostalAddress",
    addressLocality: "Dubai",
    addressCountry: "AE",
  },
  geo: {
    "@type": "GeoCoordinates",
    latitude: "25.2048",
    longitude: "55.2708",
  },
  areaServed: [
    "Dubai",
    "Singapore",
    "Malaysia",
    "Thailand",
    "Hong Kong",
    "Vietnam",
    "Philippines",
    "Cambodia",
    "Bali",
    "Indonesia",
    "Laos",
    "Japan",
  ],
  knowsAbout: [
    "MICE",
    "Incentive Travel",
    "Corporate Travel",
    "Destination Management",
    "Group Travel",
    "Leisure Travel",
  ],
  foundingDate: "2004",
  contactPoint: {
    "@type": "ContactPoint",
    telephone: "+971524526313",
    contactType: "sales",
    email: "hello@trawelldmc.com",
    areaServed: ["AE", "SG", "MY", "TH", "HK", "PH", "VN", "KH", "ID", "LA", "JP"],
    availableLanguage: ["English"],
  },
  sameAs: [
    "https://www.linkedin.com/company/trawelldmc",
    "https://www.instagram.com/trawelldmc",
  ],
};

const WEBSITE_LD = {
  "@context": "https://schema.org",
  "@type": "WebSite",
  "@id": `${SITE_URL}/#website`,
  url: SITE_URL,
  name: "Trawelldmc",
  description: DEFAULTS.description,
  inLanguage: "en",
  publisher: { "@id": `${SITE_URL}/#organization` },
};

export default function Seo({
  title,
  description,
  path = "/",
  image,
  type = "website",
  breadcrumb,
  extraJsonLd,
}) {
  // If the supplied title already mentions our brand, use it verbatim; else append the suffix.
  const fullTitle = title
    ? /trawelldmc/i.test(title)
      ? title
      : `${title} · Trawelldmc`
    : DEFAULTS.title;
  const fullDescription = description || DEFAULTS.description;
  const ogDescription = description || DEFAULTS.ogDescription;
  const twitterTitleValue = title
    ? /trawelldmc/i.test(title)
      ? title
      : `${title} | Trawelldmc`
    : DEFAULTS.twitterTitle;
  const twitterDescriptionValue = description || DEFAULTS.twitterDescription;
  const canonical = `${SITE_URL}${path}`;
  const ogImage = image || DEFAULTS.image;

  const breadcrumbLd = breadcrumb && {
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    itemListElement: breadcrumb.map((b, i) => ({
      "@type": "ListItem",
      position: i + 1,
      name: b.name,
      item: `${SITE_URL}${b.path}`,
    })),
  };

  return (
    <Helmet>
      <html lang="en" />
      <title>{fullTitle}</title>
      <meta name="description" content={fullDescription} />
      <meta name="keywords" content={DEFAULTS.keywords} />
      <meta name="author" content="Trawelldmc" />
      <meta name="robots" content="index, follow" />
      <link rel="canonical" href={canonical} />
      <link rel="alternate" hrefLang="en" href={canonical} />
      <link rel="alternate" hrefLang="x-default" href={canonical} />

      {/* Open Graph */}
      <meta property="og:type" content={type} />
      <meta property="og:site_name" content="Trawelldmc" />
      <meta property="og:title" content={fullTitle} />
      <meta property="og:description" content={ogDescription} />
      <meta property="og:url" content={canonical} />
      <meta property="og:image" content={ogImage} />
      <meta property="og:image:alt" content="Trawelldmc — Destination Management Company" />
      <meta property="og:locale" content="en_US" />

      {/* Twitter */}
      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:title" content={twitterTitleValue} />
      <meta name="twitter:description" content={twitterDescriptionValue} />
      <meta name="twitter:image" content={ogImage} />

      {/* JSON-LD (Organization + WebSite are hardcoded in index.html for crawler reliability) */}
      {breadcrumbLd && (
        <script type="application/ld+json">{JSON.stringify(breadcrumbLd)}</script>
      )}
      {extraJsonLd && (
        <script type="application/ld+json">{JSON.stringify(extraJsonLd)}</script>
      )}
    </Helmet>
  );
}
